import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Check, X, Diamond, Handshake, ShieldCheck, Globe, ChevronDown, Linkedin, Twitter, Menu,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Ticker } from "@/components/Ticker";
import { Card } from "@/components/primitives";
import { BrandSwitcher } from "@/context/CommodityContext";
import { useAuth } from "@/context/AuthContext";
import { isDataSite, dataSiteHref, goSite } from "@/lib/site";
import { DataNav } from "@/datasite/DataNav";
import { TickerRail } from "@/components/TickerRail";
import { FinalCta, ForecastBoard, LatestEdition, HomeBoard, SellCards, RegistryStrip, MethodologyStrip, CollectionStrip } from "@/components/HomeSections";

const SOCIALS = { linkedin: "#", x: "#" };

import { LaunchBanner } from "@/components/LaunchGate";

const fade = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };

const PLATFORM_LINKS = [
  ["Terminal", "/terminal", "nav-plat-terminal"],
  ["Virtus Signal", "/signal", "nav-plat-signal"],
  ["Track Record", "/track-record", "nav-plat-trackrecord"],
  ["Calendar", "/calendar", "nav-plat-calendar"],
  ["The Provenance", "/wire", "nav-plat-wire"],
  ["Africa News", "/africa-news", "nav-plat-africanews"],
  ["Africa FX", "/africa-fx", "nav-plat-africafx"],
  ["Metals Desk", "/metals-desk", "nav-plat-metals"],
  ["Market Data", "/market", "nav-plat-market"],
  ["Registry", "/registry", "nav-plat-registry"],
  ["Collection", "/collection", "nav-plat-collection"],
  ["Pricing", "/pricing", "nav-plat-pricing"],
  ["About", "/about", "nav-plat-about"],
];

export function Nav() {
  if (isDataSite()) return <DataNav />;
  return <MainNav />;
}

function MainNav() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const goLink = (to) => (to.startsWith("@data:") ? goSite(dataSiteHref(to.slice(6))) : nav(to));
  const go = (to) => { setOpen(false); goLink(to); };
  return (
    <header className="fixed top-0 inset-x-0 z-40 bb-glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/")} data-testid="nav-logo"><Logo size={26} sub={false} /></button>
          <span className="hidden md:block text-[0.56rem] uppercase tracking-[0.22em] text-bb-dim border-l border-white/10 pl-3" data-testid="nav-delaware-badge">The American Provenance Standard</span>
          <div className="hidden sm:block"><BrandSwitcher compact /></div>
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button onClick={logout} data-testid="nav-logout"
                className="hidden sm:block text-sm text-bb-text/80 hover:text-bb-gold transition-colors px-3 py-2">
                Sign Out
              </button>
              <button onClick={() => nav("/dashboard")} data-testid="nav-dashboard"
                className="hidden sm:block bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-4 py-2 text-sm font-medium">
                My Dashboard
              </button>
            </>
          ) : (
            <>
              <button onClick={() => nav("/login")} data-testid="nav-login"
                className="hidden sm:block text-sm text-bb-text/80 hover:text-bb-gold transition-colors px-3 py-2">
                Sign In
              </button>
              <button onClick={() => nav("/register")} data-testid="nav-signup"
                className="hidden sm:block bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-4 py-2 text-sm font-medium">
                Subscribe
              </button>
            </>
          )}
          <button onClick={() => setOpen(!open)} data-testid="nav-hamburger" aria-label="Menu"
            className="lg:hidden text-bb-text/85 hover:text-bb-gold transition-colors p-2">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <div className="hidden lg:block border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-5 h-9 flex items-center gap-5 overflow-x-auto">
          <span className="text-[0.58rem] uppercase tracking-[0.22em] text-bb-champagne shrink-0">Platform</span>
          {PLATFORM_LINKS.map(([label, to, tid]) => (
            <button key={to} onClick={() => goLink(to)} data-testid={tid}
              className="text-[0.72rem] text-bb-muted/80 hover:text-bb-gold transition-colors whitespace-nowrap shrink-0">
              {label}
            </button>
          ))}
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/8 bg-bb-bg/98 backdrop-blur-md max-h-[75vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="px-5 py-4 space-y-1">
            {PLATFORM_LINKS.map(([label, to, tid]) => (
              <button key={to} onClick={() => go(to)} data-testid={`m-${tid}`}
                className="block w-full text-left text-sm text-bb-text/85 hover:text-bb-gold transition-colors py-2 border-b border-white/5">
                {label}
              </button>
            ))}
            <div className="flex gap-3 pt-4">
              {user ? (
                <>
                  <button onClick={() => { setOpen(false); logout(); }} className="flex-1 border border-bb-gold/50 text-bb-gold rounded-sm py-2.5 text-sm" data-testid="m-nav-logout">Sign Out</button>
                  <button onClick={() => go("/dashboard")} className="flex-1 bg-bb-gold text-bb-bg rounded-sm py-2.5 text-sm font-medium" data-testid="m-nav-dashboard">My Dashboard</button>
                </>
              ) : (
                <>
                  <button onClick={() => go("/login")} className="flex-1 border border-bb-gold/50 text-bb-gold rounded-sm py-2.5 text-sm" data-testid="m-nav-login">Sign In</button>
                  <button onClick={() => go("/register")} className="flex-1 bg-bb-gold text-bb-bg rounded-sm py-2.5 text-sm font-medium" data-testid="m-nav-signup">Start Free Trial</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <TickerRail />
    </header>
  );
}

const COVERAGE = [
  ["Rough pricing (daily)", "y", "weekly", "x", "quarterly"],
  ["Polished pricing (daily)", "y", "weekly", "y", "x"],
  ["Industry Price Index", "y", "y", "x", "x"],
  ["Global Polished Index", "y", "x", "y", "x"],
  ["Independent Rough Index", "y", "x", "x", "partial"],
  ["Fancy Color Index", "y", "x", "x", "x"],
  ["Global producer atlas", "y", "partial", "x", "partial"],
  ["State marketer auction results", "y", "partial", "partial", "x"],
  ["State marketer contract data", "y", "x", "x", "x"],
  ["Failed / cancelled archive", "y", "partial", "x", "x"],
  ["Producer stockpile data", "y", "x", "x", "x"],
  ["Mine-level production", "y", "x", "x", "quarterly"],
  ["Fiscal revenue tracker", "y", "x", "x", "x"],
  ["Lab-grown market tracker", "y", "x", "partial", "x"],
  ["VDR Registry / chain of custody", "y", "x", "x", "x"],
  ["Direct stone ordering", "y", "x", "x", "x"],
];

function Cell({ v }) {
  if (v === "y") return <Check size={16} className="text-bb-success mx-auto" />;
  if (v === "x") return <X size={15} className="text-bb-error/70 mx-auto" />;
  return <span className="text-bb-muted text-[11px] uppercase tracking-wide">{v}</span>;
}

const HERO_MINE = "https://static.prod-images.emergentagent.com/jobs/0f4d2fdc-7d70-4a04-ac5d-89afdf4a5f38/images/d8921cb7cdd0a2aa92f62dc9362ff5804a2020fab0b47bcd026b162df29b7907.png";

function HeroSection() {
  const nav = useNavigate();
  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
        <img src={HERO_MINE} alt="Open-pit diamond mine at golden hour"
          className="absolute inset-0 w-full h-full object-cover" data-testid="hero-mine-image" />
        <div className="absolute inset-0 bg-gradient-to-b from-bb-bg/85 via-bb-bg/55 to-bb-bg" />
        <div className="absolute inset-0 bb-grid-bg opacity-20" />
        <div className="relative flex-1 flex flex-col items-center justify-center px-5 text-center pt-20">
          <motion.div initial="hidden" animate="show" variants={fade} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="font-serif text-[1rem] uppercase tracking-[0.3em] text-bb-gold" data-testid="hero-eyebrow">Virtus Registry</div>
            <h1 className="font-serif text-[2.75rem] sm:text-[3.5rem] lg:text-[4rem] font-semibold text-bb-text leading-[1.05] mt-10" style={{ letterSpacing: "-0.02em" }}>
              The American<br />Provenance Standard
            </h1>
            <p className="text-bb-secondary text-base sm:text-lg font-light mt-7 max-w-2xl mx-auto">
              Independent registry and intelligence for diamonds, precious metals, and critical minerals.
              Global data. American standard.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              <button onClick={() => nav("/wire")} data-testid="hero-provenance-btn"
                className="bg-bb-gold text-[#FFFFFF] hover:bg-bb-goldhover transition-colors rounded-sm px-7 py-3 text-[0.8125rem] font-semibold uppercase tracking-[0.06em]">
                Read The Provenance · Free
              </button>
              <button onClick={() => nav("/market")} data-testid="hero-market-btn"
                className="border border-bb-gold text-bb-champagne hover:bg-bb-gold/10 transition-colors rounded-sm px-7 py-3 text-[0.8125rem] font-semibold uppercase tracking-[0.06em]">
                See Live Market Data
              </button>
              <button onClick={() => nav("/pricing")} data-testid="hero-institutional-btn"
                className="border border-white/15 text-bb-text/85 hover:border-bb-gold/50 hover:text-bb-champagne transition-colors rounded-sm px-7 py-3 text-[0.8125rem] font-semibold uppercase tracking-[0.06em]">
                Institutional Access
              </button>
            </div>
          </motion.div>
        </div>
        <div className="relative pb-6 flex justify-center">
          <ChevronDown size={22} className="text-bb-gold/60 bb-chev" />
        </div>
        <Ticker />
      </section>
  );
}

function RdiWireSection({ indices, wire }) {
  const nav = useNavigate();
  const poly = indices?.polished_composite;
  const rdi = indices?.rough_index;
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24 grid lg:grid-cols-5 gap-6">
        <Card gold className="p-8 lg:col-span-2 flex flex-col" data-testid="polished-anchor">
          <div className="text-[0.62rem] uppercase tracking-[0.24em] text-bb-gold">The Market Anchor</div>
          <h2 className="font-serif text-3xl mt-3">VDR Polished Composite</h2>
          <div className="flex items-baseline gap-3 mt-6">
            <span className="font-serif text-6xl text-bb-gold tnum" data-testid="polished-composite-value">
              {poly ? `$${Number(poly).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "N/A"}
            </span>
            <span className="text-bb-dim text-sm">average polished $/ct · D–J · IF–SI2</span>
            {indices?.polished_composite_wow_pct != null && (
              <span className={`tnum text-sm ${indices.polished_composite_wow_pct >= 0 ? "text-bb-success" : "text-bb-error"}`} data-testid="polished-wow">
                {indices.polished_composite_wow_pct >= 0 ? "▲ +" : "▼ "}{indices.polished_composite_wow_pct}% WoW
              </span>
            )}
          </div>
          <p className="text-bb-secondary text-sm leading-relaxed mt-5 flex-1">
            The average polished reference across every color and clarity on the VDR grid, the number the
            registry quotes on certificates, orders and passports. Per-stone totals from 0.30 to 10.00 carats,
            updated daily by the VDR research desk.
          </p>
          <button onClick={() => nav("/market?tab=rough-pricing")} data-testid="home-rough-strip"
            className="mt-5 flex flex-wrap items-center justify-between gap-2 border border-white/10 hover:border-bb-gold/40 transition-colors rounded-sm px-4 py-3 text-left">
            <span className="text-[0.62rem] uppercase tracking-[0.16em] text-bb-dim">VDR Rough Diamond Index</span>
            <span className="tnum text-sm">
              <span className="text-bb-text">{rdi?.value ?? "N/A"}</span>
              {rdi && <span className={`ml-2 ${(rdi.mom ?? 0) >= 0 ? "text-bb-success" : "text-bb-error"}`}>{rdi.mom > 0 ? "+" : ""}{rdi.mom}% MoM</span>}
              <span className="ml-2 text-bb-gold text-[0.66rem] uppercase tracking-[0.1em]">Rough Desk →</span>
            </span>
          </button>
          <div className="flex flex-wrap gap-3 mt-5">
            <button onClick={() => nav("/market?tab=diamond-pricing")} data-testid="polished-pricing-cta"
              className="border border-bb-gold text-bb-gold hover:bg-bb-gold hover:text-bb-bg transition-colors rounded-sm px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.08em]">
              Open Polished Pricing
            </button>
            <button onClick={() => nav("/trade-center")} data-testid="polished-order-cta"
              className="border border-white/15 text-bb-text/85 hover:border-bb-gold/50 hover:text-bb-gold transition-colors rounded-sm px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.08em]">
              Order a Stone
            </button>
          </div>
        </Card>
        <Card className="lg:col-span-3 overflow-hidden flex flex-col" data-testid="wire-feature-block">
          <div className="p-8 pb-0">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[0.62rem] uppercase tracking-[0.24em] text-bb-gold">Market Intelligence · Virtus Data</div>
              <span className="text-[0.6rem] uppercase tracking-wide text-bb-dim border border-white/10 rounded-sm px-2 py-0.5">data.virtusregistry.com</span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl mt-4 leading-snug">{wire?.title || "The VDR Daily Wire, before US markets open"}</h2>
            <p className="text-bb-muted text-sm leading-relaxed mt-3">
              {(wire?.lede || "").slice(0, 180)} The VDR Wire (from $49/mo), the Metals Desk ($2,500/yr) and
              VDR-RDI data licensing now live on our dedicated intelligence site.
            </p>
          </div>
          <div className="p-8 pt-5 mt-auto flex flex-wrap items-center gap-3">
            <button onClick={() => nav("/wire")} data-testid="wire-feature-cta"
              className="bg-bb-gold text-bb-bg hover:bg-bb-goldhover transition-colors rounded-sm px-6 py-2.5 text-[0.72rem] font-semibold uppercase tracking-[0.06em]">
              Read Our Market Intelligence
            </button>
            <span className="text-bb-dim text-[0.66rem]">{wire?.edition_date ? `Latest edition ${wire.edition_date}` : ""} · Everything there is buyable today</span>
          </div>
        </Card>
      </section>
  );
}

function CoverageSection() {
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Coverage No One Else Offers</h2>
          <p className="text-bb-muted mt-3">A side by side of what each platform actually tracks.</p>
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left border-b border-white/8">
                <th className="p-4 font-medium text-bb-muted uppercase text-[11px] tracking-wider">Feature</th>
                <th className="p-4 text-center text-bb-gold font-serif text-base">Virtus Registry</th>
                <th className="p-4 text-center text-bb-muted">Weekly Services</th>
                <th className="p-4 text-center text-bb-muted">Index Services</th>
                <th className="p-4 text-center text-bb-muted">Producer Reports</th>
              </tr>
            </thead>
            <tbody>
              {COVERAGE.map((r) => (
                <tr key={r[0]} className="border-b border-white/5 hover:bg-white/[0.015]">
                  <td className="p-4 text-bb-text/90">{r[0]}</td>
                  <td className="p-4 text-center bg-bb-gold/[0.04]"><Cell v={r[1]} /></td>
                  <td className="p-4 text-center"><Cell v={r[2]} /></td>
                  <td className="p-4 text-center"><Cell v={r[3]} /></td>
                  <td className="p-4 text-center"><Cell v={r[4]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>
  );
}

export function TradingSection() {
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">From Intelligence to Ownership</h2>
          <p className="text-bb-muted mt-3">Buy Botswana diamonds directly, verified, traceable, Kimberley-Process compliant.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            [Diamond, "Verified Stone Inventory", "Browse VDR Registry stones and partner-consigned polished diamonds. Every stone shows full chain of custody from Jwaneng, Orapa or Letlhakane to your door."],
            [Handshake, "Trade Like a Bourse Member", "Our Trade Center mirrors the industry-standard negotiation model, offer, counter, agree, sign, pay, track. Verified parties only."],
            [ShieldCheck, "State Marketer Rough Access for Institutions", "Institutional members get VDR-assisted rough auction registration and Gaborone viewing coordination, direct access to state owned rough supply."],
          ].map(([Icon, t, d]) => (
            <Card key={t} className="p-7">
              <Icon size={20} className="text-bb-teal mb-4" />
              <h3 className="font-serif text-2xl mb-2">{t}</h3>
              <p className="text-bb-muted text-sm leading-relaxed">{d}</p>
            </Card>
          ))}
        </div>
        <p className="text-center text-bb-muted text-xs mt-7 max-w-3xl mx-auto">
          All trades are KYC-verified, Kimberley Process compliant, and settled in USD via SWIFT. WFDB arbitration available for any dispute. <span className="text-bb-champagne">(Trading Engine, launching next phase.)</span>
        </p>
      </section>
  );
}

const FOOTER_COLS = [
  ["Products", [
    ["The Provenance", "/wire"],
    ["Virtus Signal", "/signal"],
    ["Track Record", "/track-record"],
    ["Virtus Trade", "/register"],
    ["Metals Desk", "/metals-desk"],
    ["Market Data", "/market"],
    ["Trading Advisor", "/advisor"],
    ["The Yield Desk", "/yield"],
    ["VDR-RDI Index", "/vdr-rdi"],
    ["The Registry", "/registry"],
    ["The Collection", "/collection"],
  ]],
  ["Company", [
    ["About", "/about"],
    ["Contact", "/contact"],
    ["Pricing", "/pricing"],
    ["Data Standards", "/about/data"],
  ]],
  ["Legal", [
    ["Terms & Data Licensing", "/terms"],
    ["Privacy Policy", "/legal/privacy"],
    ["CCPA Notice", "/legal/ccpa"],
    ["Sanctions & Compliance", "/legal/sanctions"],
    ["Cookie Policy", "/legal/cookies"],
  ]],
];

export function SiteFooter() {
  const nav = useNavigate();
  return (
    <footer className="border-t border-white/5 bg-bb-surface/40">
      <div className="max-w-7xl mx-auto px-5 py-12">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2 max-w-sm">
            <Logo size={24} />
            <p className="text-bb-muted text-sm mt-4 leading-relaxed">
              Virtus Registry is an American provenance and market intelligence company covering
              diamonds, precious metals, and critical minerals.
            </p>
            <address className="text-bb-muted text-xs mt-4 not-italic leading-relaxed" data-testid="footer-hq">
              1946 Greenspring Drive<br />Baltimore, MD 21093<br />United States<br />
              <a href="tel:+14102522490" className="text-bb-champagne hover:text-bb-gold">+1 (410) 252-2490</a><br />
              <a href="mailto:virtusregistry@virturegistry.com" className="text-bb-champagne hover:text-bb-gold">virtusregistry@virturegistry.com</a>
            </address>
            <div className="flex gap-4 mt-4">
              <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin"
                className="flex items-center gap-2 text-bb-muted hover:text-bb-gold transition-colors text-xs">
                <Linkedin size={14} /> LinkedIn
              </a>
              <a href={SOCIALS.x} target="_blank" rel="noopener noreferrer" data-testid="footer-social-x"
                className="flex items-center gap-2 text-bb-muted hover:text-bb-gold transition-colors text-xs">
                <Twitter size={14} /> X / Twitter
              </a>
            </div>
          </div>
          {FOOTER_COLS.map(([title, links]) => (
            <div key={title} className="space-y-2 text-sm">
              <div className="text-bb-gold uppercase text-[11px] tracking-wider mb-2">{title}</div>
              {links.map(([label, to]) => (
                <div key={to} onClick={() => nav(to)} data-testid={`footer-link-${to.replace(/^\//, "").replace(/[^a-z0-9]+/gi, "-")}`}
                  className="text-bb-muted hover:text-bb-gold transition-colors cursor-pointer">{label}</div>
              ))}
              {title === "Products" && (
                <div onClick={() => goSite(dataSiteHref("/"))} data-testid="footer-data-site"
                  className="text-bb-muted hover:text-bb-gold transition-colors cursor-pointer">Virtus Data · Market Intelligence</div>
              )}
            </div>
          ))}
        </div>
        <div className="border-t border-white/5 mt-10 pt-6 flex flex-wrap justify-between gap-3 text-xs text-bb-muted">
          <span data-testid="footer-legal-line">© 2026 Virtus Registry · Baltimore, MD</span>
          <span className="flex items-center gap-1"><Globe size={12} /> Global coverage: diamonds · precious metals · critical minerals</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [wire, setWire] = useState(null);
  const [indices, setIndices] = useState(null);

  useEffect(() => {
    api.get("/public/wire").then(({ data }) => setWire(data)).catch(() => {});
    api.get("/public/price-desk").then(({ data }) => setIndices(data?.indices)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-bb-bg text-bb-text">
      <Nav />
      <div className="pt-[128px] -mt-1"><LaunchBanner /></div>
      <HeroSection />
      <div className="pt-20"><LatestEdition /></div>
      <HomeBoard />
      <SellCards />
      <RegistryStrip />
      <MethodologyStrip />
      <ForecastBoard />
      <CollectionStrip />
      <FinalCta />
      <SiteFooter />
    </div>
  );
}

